<script setup>
import ModalWrapper from '@/modals/ModalWrapper';
import { ref } from 'vue';
import { useProductStore } from '@/stores/product';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';

const props = defineProps({
    product: {
        type: Object,
        default: () => ({
            //
        })
    },
    mode: {
        type: String,
        default: 'Create'
    }
});

const emit = defineEmits(['close-modal']);
const { store, update } = useProductStore();

const optionsStore = useOptionsStore();
const { productTypeOptions, unitOptions, compoundTypeOptions } = storeToRefs(optionsStore);

const form = ref({
    ...props.product,
});

// Restore compound types for dropdown
if (props.product?.compound_types) {
    form.value.compound_types = props.product.compound_types.map(type => type.id);
}

// When creating new product, all compound types are selected by default
if (props.mode === 'Create') {
    form.value.compound_types = compoundTypeOptions.value.map(type => type.value);
}

const save = async () => {
    const action = props.mode === 'Create' ? store : update;
    await action({
        ...form.value,
    });

    emit('close-modal');
};
</script>

<template>
<ModalWrapper>
    <template #header>{{ $t('label.mode_what', { 'mode' : mode, 'what' : 'product' }) }}</template>
    <template #subheader>{{ $t('label.create_or_edit', { 'what' : 'product' }) }}</template>

    <form @submit.prevent="save">
        <div class="md:grid md:grid-cols-3 gap-x-4">
            <StyledInput
                v-model="form.name"
                name="name"
                :label="$t('label.name')"
                :placeholder="$t('label.name')" />

            <StyledDropdown
                v-model="form.type_id"
                :clearable="false"
                :label="$t('label.type')"
                :placeholder="$t('label.type')"
                :options="productTypeOptions"
                name="type_id" />

            <StyledDropdown
                v-model="form.unit_id"
                :clearable="false"
                :label="$t('label.unit')"
                :placeholder="$t('label.unit')"
                :options="unitOptions"
                name="unit_id" />

            <StyledCurrency
                v-model="form.price_per_unit"
                :config="{ precision: 6 }"  
                :placeholder="$t('label.price_per_unit')"
                name="price_per_unit"
                :label="$t('label.price_per_unit')" />

            <StyledDropdown
                v-model="form.is_active"
                :clearable="false"
                :label="$t('label.status')"
                :placeholder="$t('label.status')"
                :options="[
                    { label: $t('label.active'), value: true },
                    { label: $t('label.inactive'), value: false }
                ]"
                name="is_active" />

            <StyledInput
                v-model="form.drug_identification_number"
                name="drug_identification_number"
                :label="$t('label.din')"
                :placeholder="$t('label.din')" />
            
            <StyledDropdown
                v-model="form.compound_types"
                :clearable="false"
                :label="$t('label.applies_to_compound_types')"
                :placeholder="$t('label.compound_types')"
                :options="compoundTypeOptions"
                name="compound_types"
                class="col-span-full"
                multiple />
        </div>
    </form>

    <template #footer>
        <StyledSaveButton modal @save="save" />
    </template>
</ModalWrapper>
</template>
